import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { calculateEmployeeCoverage, calculateLearningProgress } from '@sharedInterfaces/globalHelper';
import type {
    IEmployeeSettingsCompetenceSettingsWantedCompetence,
    IEmployeeSettingsGoal,
} from '@sharedInterfaces/IEmployeeSettings';
import type { ISkillLevelCompare } from '@sharedInterfaces/IOpportunity';
import type { IReference } from '@sharedInterfaces/IReference';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import { GoalDialog } from '@src/App/Components/Dialogs/GoalDialog/GoalDialog';
import { EmployeeCoverage } from '@src/App/Components/General/EmployeeSuggestion/EmployeeSuggestions/EmployeeCoverage';
import SuggestedCompetences from '@src/App/Components/Goals/SuggestedCompetences/SuggestedCompetences';
import type { AppState } from '@store/store';
import {
    ArrowRight,
    Bot,
    Building2,
    Calendar,
    CheckCircle,
    Clock,
    HandMetal,
    Lightbulb,
    Play,
    Plus,
    ShieldUser,
    Target,
    TrendingUp,
    User,
} from 'lucide-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { EDPWantedCompetenceCard } from './EDPWantedCompetenceCard';
import { Goals } from './Goals';
import { LinearProgressWithLabel } from './LinearProgressWithLabel';
import { ProfileTabHeader } from './ProfileTabHeader';

/**
 * Function that returns a JSX.Element representing the DevelopmentTab component.
 * @returns {JSX.Element}
 */
export function DevelopmentTab(): JSX.Element {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeDevelopment;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ProfileTabHeader
                title={langStrings.planningCardTitle || 'Entwicklungsplanung'}
                subtitle="Verwalte deine beruflichen Entwicklungsziele und Kompetenzen"
            />
            <SuggestedCompetences />
            <EmployeeDevelopmentPlanningCard />
        </Box>
    );
}
/**
 * This is a component for Employee Development.
 * @returns {JSX.Element}
 */
function EmployeeDevelopmentPlanningCard(): JSX.Element {
    const theme = useTheme();
    const { openDialog } = useDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeDevelopment;
    const offline = useSelector((state: AppState) => state.client.offline);

    return (
        <Paper
            elevation={0}
            sx={{
                p: { lg: 3, md: 2, xs: 2 },
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                bgcolor: 'background.paper',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Target size={20} color={theme.palette.primary.main} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {langStrings.planningCardTitle}
                    </Typography>
                </Box>
                <IconButton
                    onClick={() => {
                        openDialog(<GoalDialog id="GoalDialog" />);
                    }}
                    disabled={offline}
                    sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                        },
                    }}
                >
                    <Plus size={16} />
                </IconButton>
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                {langStrings.planningCardDescription}
            </Typography>

            <Stack spacing={2}>
                <WantedSkills />
                <Goals />
                <WantedCompetences />
                <DevelopmentGoalsSection />
            </Stack>
        </Paper>
    );
}

/**
 * Displays the wanted skills.
 *
 * @returns {JSX.Element} The JSX element that displays the wanted skills.
 */
function WantedSkills(): JSX.Element {
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeDevelopment;
    const goals = useSelector((state: AppState) => state.employeeSettings.goals).filter((wc) => wc.state !== 'done');
    const wantedCompetences = useSelector(
        (state: AppState) => state.employeeSettings.competenceSettings.wantedCompetences,
    ).filter((wc) => wc.state !== 'done');

    const empSkills = useSelector((state: AppState) => state.employee.skills);
    const empCertificates = useSelector((state: AppState) => state.employee.certificates);
    const skills = mergeWantedSkills(wantedCompetences, goals);
    const certificates = mergeWantedCertificates(wantedCompetences, goals);

    if (skills.length === 0) return <></>;

    const { earliestCreatedAt, latestUntilDate } = extractDatesFromWantedCompetences([...wantedCompetences, ...goals]);
    const coverage = calculateEmployeeCoverage(
        skills.map((s) => ({ title: s.title, level: s.wantedLevel, id: s.id })),
        empSkills,
        certificates.map((c) => ({ title: c.title, id: c.id })),
        empCertificates,
        [],
        [],
    );

    const coverageOld = calculateEmployeeCoverage(
        skills.map((s) => ({ title: s.title, level: s.wantedLevel, id: s.id })),
        skills.map((s) => ({ title: s.title, level: s.level, id: s.id as number })),
        certificates.map((c) => ({ title: c.title, id: c.id })),
        certificates.map((c) => ({ title: c.title, id: c.id as number })),
        [],
        [],
    );

    const score = calculateLearningProgress(
        empSkills,
        coverageOld,
        skills.map((s) => ({ title: s.title, level: s.wantedLevel, id: s.id as number })),
    );

    return (
        <Paper
            elevation={0}
            sx={{
                p: { lg: 3, md: 2, xs: 2 },
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <HandMetal size={20} color={theme.palette.primary.main} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {langStrings.wantedSkillsTitle}
                </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                {langStrings.wantedSkillsHint}
            </Typography>

            <Stack spacing={2}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(200px, 1fr))' },
                        gap: 2,
                    }}
                >
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Calendar size={16} color={theme.palette.info.main} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                                {langStrings.oftenUsed.createdAt}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {earliestCreatedAt.toLocaleDateString()}
                        </Typography>
                    </Box>

                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Target size={16} color={theme.palette.warning.main} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
                                {langStrings.untilDate}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {latestUntilDate.toLocaleDateString()}
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {langStrings.learnProgress}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {Math.round(score * 100)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={score * 100}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: alpha(theme.palette.divider, 0.2),
                            '& .MuiLinearProgress-bar': {
                                bgcolor: score < 0.95 ? theme.palette.primary.main : theme.palette.success.main,
                                borderRadius: 4,
                            },
                        }}
                    />
                </Box>

                {coverage && <EmployeeCoverage expended suggestedEmployee={coverage} hideName hideAvailability />}
            </Stack>
        </Paper>
    );
}

/**
 * Represents the list of wanted competences.
 *
 * @returns {JSX.Element} The JSX element representing the list of wanted competences.
 */
function WantedCompetences() {
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeDevelopment;
    const wantedCompetences = useSelector(
        (state: AppState) => state.employeeSettings.competenceSettings.wantedCompetences,
    );

    const filteredCompetences = wantedCompetences.filter((wc) => wc.state !== 'done');

    return (
        <Paper
            elevation={0}
            sx={{
                p: { lg: 3, md: 2, xs: 2 },
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                bgcolor: 'background.paper',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ShieldUser size={20} color={theme.palette.secondary.main} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {langStrings.wantedCompetencesTitle}
                </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                {langStrings.wantedCompetencesHint}
            </Typography>

            {filteredCompetences.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <ShieldUser size={48} style={{ color: theme.palette.text.secondary, marginBottom: '16px' }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        Keine Kompetenzen geplant
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Füge deine ersten Entwicklungskompetenzen hinzu
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={2}>
                    {filteredCompetences.map((wc) => (
                        <EDPWantedCompetenceCard key={wc.id} wantedCompetence={wc} />
                    ))}
                </Stack>
            )}
        </Paper>
    );
}
export const mergeWantedSkills = (
    competences: IEmployeeSettingsCompetenceSettingsWantedCompetence[],
    goals: IEmployeeSettingsGoal[],
): ISkillLevelCompare[] => {
    const skillMap: { [key: string]: ISkillLevelCompare } = {};

    const updateSkillMap = ({ id, title, wantedLevel, level }: ISkillLevelCompare) => {
        const existingSkill = skillMap[title];

        if (existingSkill) {
            skillMap[title].wantedLevel = Math.max(existingSkill.wantedLevel, wantedLevel);
            skillMap[title].level = Math.min(existingSkill.level, level);
        } else {
            skillMap[title] = { id, title, wantedLevel, level };
        }
    };

    const processSkills = (item: IEmployeeSettingsCompetenceSettingsWantedCompetence | IEmployeeSettingsGoal) => {
        item.initialCompareState.coveredSkills.forEach(updateSkillMap);
        item.initialCompareState.missingSkills.forEach(updateSkillMap);
    };

    competences.forEach(processSkills);
    goals.forEach(processSkills);

    return Object.values(skillMap);
};

export const mergeWantedCertificates = (
    competences: IEmployeeSettingsCompetenceSettingsWantedCompetence[],
    goals: IEmployeeSettingsGoal[],
): IReference[] => {
    const certificateMap: { [key: string]: IReference } = {};

    const addCertificateToMap = (certificate: IReference) => {
        if (!certificateMap[certificate.title]) {
            certificateMap[certificate.title] = { ...certificate };
        }
    };

    const processCertificates = (item: IEmployeeSettingsCompetenceSettingsWantedCompetence | IEmployeeSettingsGoal) => {
        item.initialCompareState.coveredCertificates?.forEach(addCertificateToMap);
        item.initialCompareState.missingCertificates?.forEach(addCertificateToMap);
    };

    competences.forEach(processCertificates);
    goals.forEach(processCertificates);

    return Object.values(certificateMap);
};

interface DateResult {
    earliestCreatedAt: Date;
    latestUntilDate: Date;
}

export const extractDatesFromWantedCompetences = (
    items: { untilDate: Date; createdAt: Date }[],
): DateResult => {
    if (items.length === 0) {
        return { earliestCreatedAt: new Date(), latestUntilDate: new Date() };
    }

    let earliestCreatedAt = new Date(items[0].createdAt);
    let latestUntilDate = new Date(items[0].untilDate);

    for (const item of items) {
        const created = new Date(item.createdAt);
        const until = new Date(item.untilDate);
        if (created < earliestCreatedAt) earliestCreatedAt = created;
        if (until > latestUntilDate) latestUntilDate = until;
    }

    return { earliestCreatedAt, latestUntilDate };
};

// Development Goals Section (moved from DevelopmentGoalsTab)
interface DevelopmentGoal {
    id: number;
    title: string;
    description: string;
    category: 'skill' | 'certificate' | 'competence' | 'career';
    status: 'active' | 'suggested' | 'completed' | 'planned';
    source: 'manager' | 'ai' | 'hr' | 'self';
    progress: number;
    dueDate?: string;
    managerName?: string;
    estimatedTime?: string;
    difficulty?: string;
    totalModules?: number;
    completedModules?: number;
}

function DevelopmentGoalsSection() {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(0);

    // Mock data - replace with real backend data
    const mockGoals: DevelopmentGoal[] = [
        {
            id: 1,
            title: 'Full Stack Developer Path',
            description: 'Von Frontend zu Backend - werde zum kompletten Entwickler',
            category: 'skill',
            status: 'active',
            source: 'self',
            progress: 65,
            estimatedTime: '6 Wochen',
            difficulty: 'Intermediate',
            totalModules: 12,
            completedModules: 8,
        },
        {
            id: 2,
            title: 'React Advanced Patterns',
            description: 'Meistere fortgeschrittene React Patterns für bessere Code-Architektur',
            category: 'skill',
            status: 'suggested',
            source: 'manager',
            progress: 0,
            managerName: 'Sarah Schmidt',
            estimatedTime: '4 Wochen',
            difficulty: 'Advanced',
        },
        {
            id: 3,
            title: 'Cloud Architecture Mastery',
            description: 'AWS, Azure und Google Cloud - beherrsche alle großen Plattformen',
            category: 'certificate',
            status: 'suggested',
            source: 'ai',
            progress: 0,
            estimatedTime: '8 Wochen',
            difficulty: 'Advanced',
            totalModules: 15,
            completedModules: 0,
        },
        {
            id: 4,
            title: 'Agile Leadership',
            description: 'Führe Teams erfolgreich in agilen Umgebungen',
            category: 'competence',
            status: 'completed',
            source: 'hr',
            progress: 100,
            estimatedTime: '4 Wochen',
            difficulty: 'Intermediate',
            totalModules: 8,
            completedModules: 8,
        },
        {
            id: 5,
            title: 'TypeScript Zertifizierung',
            description: 'Erlange die offizielle TypeScript Zertifizierung',
            category: 'certificate',
            status: 'planned',
            source: 'self',
            progress: 0,
            estimatedTime: '3 Wochen',
            difficulty: 'Intermediate',
        },
    ];

    const getSourceBadge = (source: string, managerName?: string) => {
        switch (source) {
            case 'manager':
                return (
                    <Chip
                        icon={<User size={14} />}
                        label={managerName || 'Manager'}
                        size="small"
                        sx={{
                            bgcolor: alpha(theme.palette.warning.main, 0.2),
                            color: theme.palette.warning.main,
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                        }}
                    />
                );
            case 'ai':
                return (
                    <Chip
                        icon={<Bot size={14} />}
                        label="T4lo AI"
                        size="small"
                        sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                            color: theme.palette.primary.main,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        }}
                    />
                );
            case 'hr':
                return (
                    <Chip
                        icon={<Building2 size={14} />}
                        label="HR"
                        size="small"
                        sx={{
                            bgcolor: alpha(theme.palette.secondary.main, 0.2),
                            color: theme.palette.secondary.main,
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                        }}
                    />
                );
            default:
                return (
                    <Chip
                        icon={<Target size={14} />}
                        label="Eigenes Ziel"
                        size="small"
                        sx={{
                            bgcolor: alpha(theme.palette.info.main, 0.2),
                            color: theme.palette.info.main,
                            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                        }}
                    />
                );
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed':
                return <CheckCircle size={16} color={theme.palette.success.main} />;
            case 'active':
                return <Clock size={16} color={theme.palette.primary.main} />;
            case 'suggested':
                return <Lightbulb size={16} color={theme.palette.warning.main} />;
            default:
                return <Target size={16} color={theme.palette.info.main} />;
        }
    };

    const filterGoalsByTab = (tabIndex: number) => {
        switch (tabIndex) {
            case 0:
                return mockGoals.filter((g) => g.status === 'active');
            case 1:
                return mockGoals.filter((g) => g.status === 'suggested');
            case 2:
                return mockGoals.filter((g) => g.status === 'planned');
            case 3:
                return mockGoals.filter((g) => g.status === 'completed');
            default:
                return mockGoals;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return theme.palette.success.main;
            case 'active':
                return theme.palette.primary.main;
            case 'suggested':
                return theme.palette.warning.main;
            case 'planned':
                return theme.palette.info.main;
            default:
                return theme.palette.grey[500];
        }
    };

    const GoalCard = ({ goal }: { goal: DevelopmentGoal }) => {
        const statusColor = getStatusColor(goal.status);

        return (
            <Card
                elevation={0}
                sx={{
                    borderRadius: 2,
                    background: alpha(statusColor, 0.05),
                    border: `1px solid ${alpha(statusColor, 0.2)}`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                        boxShadow: theme.shadows[4],
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease-in-out',
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: statusColor,
                    },
                }}
            >
                <CardContent>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {goal.title}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                                {goal.description}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                            <Chip
                                icon={getStatusIcon(goal.status)}
                                label={
                                    goal.status === 'active'
                                        ? 'In Arbeit'
                                        : goal.status === 'suggested'
                                          ? 'Vorgeschlagen'
                                          : goal.status === 'completed'
                                            ? 'Abgeschlossen'
                                            : 'Geplant'
                                }
                                size="small"
                                sx={{
                                    bgcolor: alpha(statusColor, 0.2),
                                    color: statusColor,
                                    fontWeight: 500,
                                }}
                            />
                            {getSourceBadge(goal.source, goal.managerName)}
                        </Box>
                    </Box>

                    {/* Progress */}
                    {(goal.status === 'active' || goal.status === 'completed') && goal.totalModules && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption">
                                    Fortschritt: {goal.completedModules || 0}/{goal.totalModules} Module
                                </Typography>
                                <Typography variant="caption">{goal.progress}%</Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={goal.progress}
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: alpha(statusColor, 0.1),
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: statusColor,
                                        borderRadius: 3,
                                    },
                                }}
                            />
                        </Box>
                    )}

                    {/* Meta Info */}
                    {(goal.estimatedTime || goal.difficulty) && (
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            {goal.estimatedTime && (
                                <Chip
                                    label={`⏱️ ${goal.estimatedTime}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                />
                            )}
                            {goal.difficulty && (
                                <Chip
                                    label={`📊 ${goal.difficulty}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                />
                            )}
                        </Box>
                    )}

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {goal.status === 'suggested' && (
                            <>
                                <Button variant="contained" size="small" endIcon={<ArrowRight size={14} />}>
                                    Annehmen
                                </Button>
                                <Button variant="outlined" size="small">
                                    Ablehnen
                                </Button>
                            </>
                        )}
                        {goal.status === 'active' && (
                            <Button variant="contained" size="small" endIcon={<ArrowRight size={14} />}>
                                Fortsetzen
                            </Button>
                        )}
                        {goal.status === 'planned' && (
                            <Button variant="contained" size="small" endIcon={<ArrowRight size={14} />}>
                                Starten
                            </Button>
                        )}
                        {goal.status === 'completed' && (
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                endIcon={<CheckCircle size={14} />}
                            >
                                Zertifikat anzeigen
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>
        );
    };

    return (
        <Paper
            elevation={0}
            sx={{ p: { lg: 3, md: 2, xs: 2 }, border: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}
        >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>
                Entwicklungsziele
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
                Verwalte deine beruflichen Entwicklungsziele und verfolge deinen Fortschritt
            </Typography>

            {/* Status Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    sx={{
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 400,
                            '&.Mui-selected': {
                                fontWeight: 500,
                            },
                        },
                    }}
                >
                    <Tab label="In Arbeit" />
                    <Tab label="Vorgeschlagen" />
                    <Tab label="Geplant" />
                    <Tab label="Abgeschlossen" />
                </Tabs>
            </Box>

            {/* Goals Grid */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                {filterGoalsByTab(activeTab).map((goal) => (
                    <GoalCard key={goal.id} goal={goal} />
                ))}
            </Box>

            {/* Add New Goal Button */}
            <Button variant="contained" startIcon={<Plus size={16} />}>
                Neues Ziel hinzufügen
            </Button>
        </Paper>
    );
}
